/**
 * Game events and roll records (§7.4, §9.2).
 *
 * Events are the append-only log that drives intel reports, AAR, replay, and
 * dispute resolution. Every event carries a `visibleTo` tag list, which is what
 * `game_events.visible_to` stores and what M2's intel-report generator filters
 * on. **Tag events at emission**, not at read time: the emitter is the only place
 * that knows why something is visible.
 */
export const GM_ONLY = Object.freeze(['GM']);
export function seatTag(team, cell) {
    return `${team}:${cell}`;
}
export const GAME_EVENT_TYPES = [
    'TURN_RESOLVED',
    'ORDER_INVALIDATED',
    'MISSION_RESOLVED',
    'DETECTION_CHANGED',
    'CHARACTERIZED',
    'PHANTOM_CREATED',
    'PHANTOM_REMOVED',
    'PHANTOM_EXPIRED',
    'SEARCH_EXHAUSTED',
    'ASSET_MOVED',
    'TRANSFER_DEPARTED',
    'TRANSFER_ARRIVED',
    'DELTAV_SPENT',
    'DELTAV_EXHAUSTED',
    'STATUS_CHANGED',
    'CONTROL_LOST',
    'CONTROL_RESTORED',
    'TTC_REASSIGNED',
    'DEPLOYED',
    'SCENARIO_DEFECT',
    // Cyber — RB §5.6 (M3)
    'CYBER_ACCESS_CHANGED',
    'CYBER_ACTION_DETECTED',
    'CYBER_PAYLOAD_DEPLOYED',
    'CYBER_HUNT_REPORT',
    'CYBER_HARDENING_CHANGED',
    'CYBER_ISOLATION_CHANGED',
    'CYBER_RECOVERY_ACCELERATED',
    'ORDER_HIJACKED',
    // Electromagnetic — RB §5.5 (M3)
    'EFFECT_APPLIED',
    'EFFECT_LAPSED',
    'EFFECT_ATTRIBUTED',
    'PROTECTION_ESTABLISHED',
    // Kinetic — RB §5.7 (M3)
    'WEAPON_LAUNCHED',
    'WEAPON_ARRIVED',
    'WEAPON_INTERCEPTED',
    'INTERCEPTION_FAILED',
    'ENGAGEMENT_RESOLVED',
    'EVADE_TRIGGERED',
    'TERMINAL_ACQUISITION_FAILED',
    'ASSET_DESTROYED',
    'BELIEF_CHANGED',
    'MW_EVENT_CREATED',
    // Missile warning — RB §5.3 (M4)
    'MW_STAGE_REVEALED',
    'MW_IDENTIFIED',
    'MW_RELEASED',
    'MW_BENIGN_ENGAGED',
    // Environment — RB §8 (M3)
    'CONJUNCTION',
    'DEBRIS_CHANGED',
    'WEATHER_FORECAST',
    // Escalation — RB §7 (M3)
    'ESCALATION_BANKED',
    'ESCALATION_APPLIED',
    'ESCALATION_DECAYED',
    'ESCALATION_LEVEL_CHANGED',
    'ROE_VIOLATION',
];
/** Why an order that was legal at submission is illegal at resolution (§7.3). */
export const ORDER_INVALID_REASONS = [
    'ASSET_NOT_FOUND',
    'ASSET_NOT_OWNED',
    'ASSET_WRONG_CELL',
    'ASSET_DESTROYED',
    'ASSET_OFFLINE',
    'ASSET_UNCONTROLLABLE',
    'ASSET_IN_TRANSIT',
    'FORBIDDEN_WHILE_DEGRADED',
    'CAPACITY_EXCEEDED',
    'INSUFFICIENT_DELTAV',
    'TARGET_NOT_FOUND',
    'TARGET_RUNG_TOO_LOW',
    'TARGET_NOT_EMITTING',
    'TARGET_OUT_OF_RANGE',
    'TARGET_WRONG_KIND',
    'TARGET_NOT_ELIGIBLE',
    'SENSOR_CANNOT_PERFORM',
    'WEATHER_PROHIBITIVE',
    'MISSION_NOT_LEGAL_FOR_TYPE',
    'MISSION_NOT_IMPLEMENTED',
    'MAGAZINE_EMPTY',
    'BAD_PARAMETERS',
    // M3
    'ASSET_ISOLATED',
    'NO_ACCESS',
    'ACCESS_TOO_LOW',
    'TARGET_NOT_ON_NETWORK',
    'NO_MW_RELEASE',
    'ORDER_HIJACKED',
    /** Only when a scenario ROE rule sets `hardBlock` (decision M3-D13). */
    'ROE_FORBIDDEN',
];
/**
 * Non-fatal notes on a *valid* order (decision M3-D13).
 *
 * ROE is priced, not forbidden: RB §7.3 costs a violation 3 VP and doubles the
 * escalation, and pricing the transgression is the training point. A warning is how
 * the crew finds out before they commit rather than in the after-action review.
 */
export const ORDER_WARNINGS = ['ROE_VIOLATION'];
//# sourceMappingURL=events.js.map